import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, PageTitle, CardBase, HStack, VStack,
  Button, SelectBox, TextField, Text, Note, Breadcrumbs,
  MessageBlock, FloatingMessageBlock, TabBar, TaskDialog, StatusIcon,
} from '@freee_jp/vibes';
import {
  fetchFilebox, approveClassification, approveFilebox, rejectFilebox, modifyFilebox, fetchFileboxSummary,
  FileboxClassification, ClassifiedType, TYPE_LABELS,
} from '../api/filebox';

// Unified status lifecycle (ARICA + filebox 共通) — 表示語は統一語彙 (lib/statusVocab.ts) の approve系正規形
// pending              → A2A分類完了、対応待ち(分類承認) (stage 1)
// classification_approved → 分類承認済み、Claude が仕訳生成中
// deal_proposed        → 仕訳提案生成済み、対応待ち(仕訳承認) (stage 2)
// modified             → 修正依頼中
// approved             → 仕訳承認済み、freee登録処理中
// processed            → 登録完了
// rejected             → 却下
const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'pending', label: '対応待ち(分類承認)' },
  { key: 'classification_approved', label: '仕訳生成中' },
  { key: 'deal_proposed', label: '対応待ち(仕訳承認)' },
  { key: 'modified', label: '修正依頼中' },
  { key: 'processed', label: '登録完了' },
  { key: 'rejected', label: '却下' },
];

const TYPE_OPTIONS = [
  { value: '', name: '全て' },
  ...(Object.keys(TYPE_LABELS) as ClassifiedType[]).map((k) => ({ value: k, name: TYPE_LABELS[k] })),
];

const TYPE_OPTIONS_MODIFY = (Object.keys(TYPE_LABELS) as ClassifiedType[]).map((k) => ({
  value: k,
  name: TYPE_LABELS[k],
}));

const SOURCE_LABELS: Record<string, string> = {
  arica_email: 'ARICA',
  filebox: 'ファイルボックス',
  manual: '手動登録',
};

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'notice' | 'info';
  text: string;
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'approve-class'; id: number; classified_type: ClassifiedType }
  | { kind: 'approve-deal'; id: number }
  | { kind: 'reject'; id: number }
  | { kind: 'modify'; id: number; currentType: ClassifiedType };

type StatusIconType = 'success' | 'warning' | 'error' | 'progress';

function statusToIconType(status: string): StatusIconType {
  switch (status) {
    case 'processed':               return 'success';
    case 'classification_approved': return 'progress';
    case 'deal_proposed':           return 'progress';
    case 'approved':                return 'progress';
    case 'modified':                return 'warning';
    case 'rejected':                return 'error';
    default:                        return 'progress';
  }
}

export function FileboxProcessingPage() {
  const [items, setItems] = useState<FileboxClassification[]>([]);
  const [statusIdx, setStatusIdx] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [summary, setSummary] = useState<{ classified_type: string; status: string; n: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
  const [dialogInput, setDialogInput] = useState('');
  const [dialogTypeChoice, setDialogTypeChoice] = useState<ClassifiedType>('unknown');
  const [submitting, setSubmitting] = useState(false);

  const status = STATUS_TABS[statusIdx].key;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, sum] = await Promise.all([
        fetchFilebox(status, typeFilter || undefined),
        fetchFileboxSummary(),
      ]);
      setItems(list);
      setSummary(sum);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [status, typeFilter]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const pushToast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setToasts((prev) => [...prev, { ...t, id }]);
    setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), 5000);
  }, []);

  const openApproveClass = (id: number, t: ClassifiedType) => setDialog({ kind: 'approve-class', id, classified_type: t });
  const openApproveDeal = (id: number) => setDialog({ kind: 'approve-deal', id });
  const openReject = (id: number) => { setDialogInput(''); setDialog({ kind: 'reject', id }); };
  const openModify = (id: number, currentType: ClassifiedType) => {
    setDialogInput(''); setDialogTypeChoice(currentType);
    setDialog({ kind: 'modify', id, currentType });
  };
  const closeDialog = () => setDialog({ kind: 'closed' });

  const confirmApproveClass = async () => {
    if (dialog.kind !== 'approve-class') return;
    const id = dialog.id;
    setSubmitting(true);
    try {
      await approveClassification(id);
      pushToast({ kind: 'success', text: `#${id} 分類承認 (Claudeが仕訳生成を開始します)` });
      closeDialog();
      load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      pushToast({ kind: 'error', text: `分類承認失敗: ${msg}` });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmApproveDeal = async () => {
    if (dialog.kind !== 'approve-deal') return;
    const id = dialog.id;
    setSubmitting(true);
    try {
      await approveFilebox(id);
      pushToast({ kind: 'success', text: `#${id} 仕訳承認 (freee登録を開始します)` });
      closeDialog();
      load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      pushToast({ kind: 'error', text: `仕訳承認失敗: ${msg}` });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmReject = async () => {
    if (dialog.kind !== 'reject') return;
    const id = dialog.id;
    const reason = dialogInput.trim();
    if (!reason) return;
    setSubmitting(true);
    try {
      await rejectFilebox(id, reason);
      pushToast({ kind: 'success', text: `#${id} を却下しました` });
      closeDialog();
      load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      pushToast({ kind: 'error', text: `却下失敗: ${msg}` });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmModify = async () => {
    if (dialog.kind !== 'modify') return;
    const id = dialog.id;
    const reason = dialogInput.trim();
    setSubmitting(true);
    try {
      await modifyFilebox(id, { classified_type: dialogTypeChoice, reason });
      pushToast({ kind: 'success', text: `#${id} を修正依頼 (${TYPE_LABELS[dialogTypeChoice]})` });
      closeDialog();
      load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      pushToast({ kind: 'error', text: `修正失敗: ${msg}` });
    } finally {
      setSubmitting(false);
    }
  };

  const statusCount = useCallback((t: string): number =>
    summary.filter((s) => s.status === t).reduce((a, b) => a + b.n, 0),
  [summary]);

  const tabLabels = useMemo(
    () => STATUS_TABS.map((t) => `${t.label} (${statusCount(t.key)})`),
    [statusCount],
  );

  return (
    <Container>
      <Breadcrumbs links={[{ title: 'FREEEMAN', url: '/' }, { title: 'ファイルボックス処理' }]} />
      <PageTitle>ファイルボックス処理</PageTitle>

      <CardBase paddingSize="small" mb={1}>
        <TabBar
          tabs={tabLabels}
          currentTabIndex={statusIdx}
          onClickTab={setStatusIdx}
        />
        <HStack gap={1} alignItems="center" mt={1}>
          <SelectBox
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            width="medium"
            small
            label="タイプ絞込"
          />
          <Button appearance="secondary" small onClick={load} disabled={loading}>
            {loading ? '読込中…' : '再読込'}
          </Button>
          <div style={{ marginLeft: 'auto' }}>
            <Note>{items.length}件表示中</Note>
          </div>
        </HStack>
      </CardBase>

      {error && <MessageBlock error mb={1}>{error}</MessageBlock>}

      {items.length === 0 && !loading && (
        <MessageBlock mb={1}>
          該当データなし (status={status}{typeFilter ? `, type=${typeFilter}` : ''})
        </MessageBlock>
      )}

      <VStack gap={1}>
        {items.map((it) => (
          <FileboxCard
            key={it.id}
            item={it}
            onApproveClass={() => openApproveClass(it.id, it.classified_type)}
            onApproveDeal={() => openApproveDeal(it.id)}
            onReject={() => openReject(it.id)}
            onModify={() => openModify(it.id, it.classified_type)}
          />
        ))}
      </VStack>

      {toasts.map((t) => (
        <FloatingMessageBlock
          key={t.id}
          success={t.kind === 'success'}
          error={t.kind === 'error'}
          notice={t.kind === 'notice'}
          info={t.kind === 'info'}
          onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
        >
          {t.text}
        </FloatingMessageBlock>
      ))}

      {/* Stage 1: Classification approval */}
      <TaskDialog
        isOpen={dialog.kind === 'approve-class'}
        title="分類の承認 (ステージ 1/2)"
        primaryButtonLabel="分類を承認"
        closeButtonLabel="キャンセル"
        onPrimaryAction={confirmApproveClass}
        onRequestClose={closeDialog}
        disabled={submitting}
        shouldCloseOnOverlayClickOrEsc={!submitting}
      >
        <VStack gap={1}>
          <Text>
            証憑タイプ: <strong>{dialog.kind === 'approve-class' ? TYPE_LABELS[dialog.classified_type] : ''}</strong>
          </Text>
          <Text>
            この分類で承認すると、Claude が該当 skill (dispatch 表参照) で仕訳案を生成します。生成後に改めて仕訳承認が必要です。
          </Text>
          <Note>承認後の status: pending → classification_approved → (Claude 生成) → deal_proposed</Note>
        </VStack>
      </TaskDialog>

      {/* Stage 2: Deal approval */}
      <TaskDialog
        isOpen={dialog.kind === 'approve-deal'}
        title="仕訳の承認 (ステージ 2/2)"
        primaryButtonLabel="仕訳を承認 → freee登録"
        closeButtonLabel="キャンセル"
        onPrimaryAction={confirmApproveDeal}
        onRequestClose={closeDialog}
        disabled={submitting}
        shouldCloseOnOverlayClickOrEsc={!submitting}
      >
        <Text>
          提案された仕訳で freee 登録します。続行しますか?
        </Text>
      </TaskDialog>

      {/* Reject */}
      <TaskDialog
        isOpen={dialog.kind === 'reject'}
        title="却下"
        primaryButtonLabel="却下"
        closeButtonLabel="キャンセル"
        onPrimaryAction={confirmReject}
        onRequestClose={closeDialog}
        disabled={submitting}
        suspend={!dialogInput.trim()}
        danger
        shouldCloseOnOverlayClickOrEsc={!submitting}
      >
        <VStack gap={1}>
          <Text>却下理由を入力してください:</Text>
          <TextField
            value={dialogInput}
            onChange={(e) => setDialogInput(e.target.value)}
            placeholder="例: 文字が不鮮明で再スキャン必要"
            width="full"
          />
        </VStack>
      </TaskDialog>

      {/* Modify */}
      <TaskDialog
        isOpen={dialog.kind === 'modify'}
        title="修正依頼"
        primaryButtonLabel="修正依頼"
        closeButtonLabel="キャンセル"
        onPrimaryAction={confirmModify}
        onRequestClose={closeDialog}
        disabled={submitting}
        shouldCloseOnOverlayClickOrEsc={!submitting}
      >
        <VStack gap={1}>
          <SelectBox
            options={TYPE_OPTIONS_MODIFY}
            value={dialogTypeChoice}
            onChange={(e) => setDialogTypeChoice(e.target.value as ClassifiedType)}
            label="新しい分類タイプ"
            width="large"
          />
          <TextField
            value={dialogInput}
            onChange={(e) => setDialogInput(e.target.value)}
            placeholder="修正理由 (任意)"
            label="理由"
            width="full"
          />
          <Note>
            タイプを変更すると自動的に Claude 側で再分類 (Reader A/B/C 再実行) が走ります。
          </Note>
        </VStack>
      </TaskDialog>
    </Container>
  );
}

interface FileboxCardProps {
  item: FileboxClassification;
  onApproveClass: () => void;
  onApproveDeal: () => void;
  onReject: () => void;
  onModify: () => void;
}

function FileboxCard({ item, onApproveClass, onApproveDeal, onReject, onModify }: FileboxCardProps) {
  const deal = item.predicted_deal_json || {};
  const details = Array.isArray(deal.details) ? deal.details : [];
  const confidence = item.classification_confidence;

  const isAricaSource = item.source_type === 'arica_email';
  const freeeReceiptUrl = isAricaSource
    ? (item.source_pdf_url || '')
    : `https://secure.freee.co.jp/receipts/${item.freee_receipt_id}`;

  const confBadge = confidence === 'high' ? '●' : confidence === 'medium' ? '◐' : confidence === 'low' ? '○' : '○';
  const confColor = confidence === 'high' ? '#2e7d32' : confidence === 'medium' ? '#f9a825' : confidence === 'low' ? '#c62828' : '#888';
  const iconType = statusToIconType(item.status);
  const sourceLabel = SOURCE_LABELS[item.source_type || 'filebox'] || item.source_type || '—';

  // Stage-aware button visibility
  const stage1Actionable = item.status === 'pending' || item.status === 'modified';
  const stage2Actionable = item.status === 'deal_proposed';
  const hasDealProposal = item.status === 'deal_proposed' || item.status === 'approved' || item.status === 'processed';

  return (
    <CardBase paddingSize="small">
      <HStack gap={2} alignItems="start">
        <div style={{ flex: 1 }}>
          <VStack gap={1}>
            <HStack gap={1} alignItems="center">
              <Text weight="bold" size={1}>
                <span style={{ color: confColor, marginRight: 4 }}>{confBadge}</span>
                {TYPE_LABELS[item.classified_type] || item.classified_type}
              </Text>
              <StatusIcon type={iconType}>{item.status}</StatusIcon>
              <span style={{
                padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                background: isAricaSource ? '#fff4e0' : '#e0f4fc',
                color: isAricaSource ? '#a85d00' : '#006aa8',
              }}>
                {sourceLabel}
              </span>
              <Note>#{item.id} / receipt_id: {item.freee_receipt_id}</Note>
            </HStack>

            <HStack gap={2} alignItems="start">
              <div style={{ minWidth: 180 }}>
                <VStack gap={0.25}>
                  <Note>メタデータ</Note>
                  <Text size={0.875}>{item.receipt_partner_name || '—'}</Text>
                  <Text size={0.875}>{item.receipt_issue_date || '—'}</Text>
                  <Text size={0.875} weight="bold">
                    {item.receipt_amount != null ? `¥${item.receipt_amount.toLocaleString()}` : '—'}
                  </Text>
                </VStack>
              </div>

              <div style={{ flex: 1 }}>
                <VStack gap={0.25}>
                  <Note>{hasDealProposal ? '予測仕訳' : '分類承認後に仕訳生成'}</Note>
                  {details.length === 0 ? (
                    <Text size={0.875}>{hasDealProposal ? '予測データなし' : '—'}</Text>
                  ) : (
                    details.map((d: Record<string, unknown>, i: number) => (
                      <Text key={i} size={0.875}>
                        {String(d.sectionName || '?')} / {String(d.accountName || '?')} / {String(d.taxCodeName || '')} / ¥{Number(d.amount || 0).toLocaleString()}
                      </Text>
                    ))
                  )}
                  {item.predicted_section_reason && (
                    <Note>判別根拠: {item.predicted_section_reason}</Note>
                  )}
                </VStack>
              </div>

              <div style={{ minWidth: 140 }}>
                <VStack gap={0.25}>
                  <Note>A2A verdict</Note>
                  <Text size={0.875}>{item.reconcile_verdict || '—'}</Text>
                  {item.routed_to_ref && (
                    <>
                      <Note>routed →</Note>
                      <Text size={0.875}>{item.routed_to_ref}</Text>
                    </>
                  )}
                </VStack>
              </div>
            </HStack>

            {item.rejection_reason && (
              <MessageBlock error>
                却下理由: {item.rejection_reason}
              </MessageBlock>
            )}
          </VStack>
        </div>

        <div style={{ minWidth: 150 }}>
          <VStack gap={0.5}>
            {freeeReceiptUrl && (
              <Button appearance="secondary" small href={freeeReceiptUrl} target="_blank" rel="noreferrer">
                証憑を表示
              </Button>
            )}

            {/* Stage 1: classification approval */}
            {stage1Actionable && (
              <>
                <Button appearance="primary" small onClick={onApproveClass}>
                  分類を承認 (1/2)
                </Button>
                <Button appearance="secondary" small onClick={onModify}>分類を修正</Button>
                <Button appearance="secondary" small danger onClick={onReject}>却下</Button>
              </>
            )}

            {/* Stage 2: deal approval */}
            {stage2Actionable && (
              <>
                <Button appearance="primary" small onClick={onApproveDeal}>
                  仕訳を承認 (2/2)
                </Button>
                <Button appearance="secondary" small onClick={onModify}>仕訳を修正</Button>
                <Button appearance="secondary" small danger onClick={onReject}>却下</Button>
              </>
            )}
          </VStack>
        </div>
      </HStack>
    </CardBase>
  );
}
